import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Header from '../report_page_universal/ReportHeader'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import ReportFooter from './solution_base_report/SolutionReportFooter'
import { useAuth } from '../../../../context/AuthContext'

import ReportDocument from '../report_page_universal/ReportDocument'
import UniversalLoader from '../../../ui_reusable/UniversalLoader'
import type { SolutionReportStore } from './types/solutionReportStoreTypes'
import type {
  RouteParams,
  RouteTowardsReportPackage,
} from '../../types/ReportTypes'
import type { UseBoundStore, StoreApi } from 'zustand'

/**
 * Report operational modes
 * These constants define the different ways the report component can operate:
 *
 * NEW_MODEL: Creating a report for a newly generated model
 * VIEW_REPORT: Viewing an existing report (read mode)
 * RERUN_MODEL: Creating a new report from a rerun of an existing model
 * EDIT_MODEL: Editing an existing model and updating its report
 */

interface SolutionBaseReportProps {
  reportTitle: string
  injectionConfig: Record<string, any>
  componentRegistry: Record<string, React.ComponentType<any>>
  store: UseBoundStore<StoreApi<SolutionReportStore>> // Properly typed store hook
}

export const SolutionBaseReport: React.FC<SolutionBaseReportProps> = ({
  reportTitle,
  injectionConfig,
  componentRegistry,
  store,
}) => {
  // Access route information and navigation
  const location = useLocation()
  const params = useParams<RouteParams>()

  // Get store functions and values using selectors to avoid unnecessary re-renders
  const resetAllStoreData = store(
    (state: SolutionReportStore) => state.resetAllStoreData,
  )
  const initializeFromNavigation = store(
    (state: SolutionReportStore) => state.initializeFromNavigation,
  )
  const setUserName = store((state: SolutionReportStore) => state.setUserName)

  // Get initial report data for the Header component
  const initialReportData = store(
    (state: SolutionReportStore) => state.anyReport,
  )

  // Get report document for the ReportDocument component
  const reportDocument = store((state: SolutionReportStore) =>
    state.getReportDocument(),
  )

  // Get loading state from store
  const loading = store(
    (state: SolutionReportStore) => state.fetchingReportIsLoading,
  )

  // Utility hooks for notifications and auth
  const { pushToast } = useStandardToaster()
  const { user } = useAuth()

  /**
   * Extract navigation parameters and destructure location state
   */
  const routedReportId = params.reportId
  const locationState = location.state as RouteTowardsReportPackage | null
  const {
    data: preLoadedReport,
    origin,
    mode: routeMode,
    modelData,
    oldModelData,
    oldArtifactId,
    newArtifactId,
  } = locationState || {}

  // Calculate final origin for navigation
  const defaultOrigin = routedReportId ? '/insights/saved_reports' : '/models'
  const crntOrigin = origin || defaultOrigin

  /**
   * Reset store state when component mounts to ensure clean state
   */
  useEffect(() => {
    resetAllStoreData()
    /* eslint-disable-next-line */
  }, []) // Empty dependency array means this runs only on mount

  /**
   * Initialize user name in store when user is available
   */
  useEffect(() => {
    if (user?.name) {
      setUserName(user.name)
    }
  }, [user?.name, setUserName])

  /**
   * Initialize the store with all navigation data
   * This handles mode determination, data loading, and state setup
   */
  useEffect(() => {
    initializeFromNavigation({
      data: preLoadedReport,
      mode: routeMode,
      modelData,
      oldModelData,
      routedReportId,
      pushToast,
      oldArtifactId,
      newArtifactId,
    })
    /* eslint-disable-next-line */
  }, [preLoadedReport, routeMode, modelData, oldModelData, routedReportId, oldArtifactId, newArtifactId])

  useEffect(() => {
    // Scrolls the window to the top left corner
    window.scrollTo(0, 0)
  }, [])

  // Render the full report with header, content and footer
  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50vh',
            zIndex: 1000,
          }}
        >
          <UniversalLoader style={{ borderRadius: '1rem' }} show={true} />
        </div>
      )}
      <Header data={initialReportData} store={store} />
      {reportDocument && (
        <ReportDocument
          isNewlyGeneratedReport={!routedReportId}
          injectionConfig={injectionConfig} // Pass solution-specific injection config for custom components
          componentRegistry={componentRegistry} // Pass custom component registry
          reportTitle={reportTitle} // Pass report-specific title
          store={store}
        />
      )}
      <ReportFooter origin={crntOrigin} store={store} />
    </div>
  )
}

export default SolutionBaseReport
